import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Get redirect URL from query params (e.g., for invite acceptance)
  const redirectUrl = searchParams.get('redirect');
  const emailConfirmed = searchParams.get('confirmed') === 'true';
  const emailRedirectTo = `${window.location.origin}/login?confirmed=true`;

  const handleChange = (e) => {
    setNeedsEmailVerification(false);
    setResendMessage(null);
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setNeedsEmailVerification(false);
    setResendMessage(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw signInError;

      // Successful login - redirect to intended page or dashboard
      const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/dashboard';
      navigate(targetUrl);
    } catch (err) {
      if (err.message?.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
      } else if (err.message?.includes('Email not confirmed')) {
        setNeedsEmailVerification(true);
        setError('Check your email to verify your GreenQuote account before logging in.');
      } else {
        setError(err.message || 'An error occurred during login');
      }
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError('Enter your email address first, then resend the verification email.');
      return;
    }

    try {
      setResending(true);
      setResendMessage(null);

      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
        options: {
          emailRedirectTo,
        },
      });

      if (resendError) throw resendError;

      setResendMessage('Verification email sent again. Check your inbox, spam, or promotions folder.');
    } catch (err) {
      setError(err.message || 'Unable to resend the verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-emerald-700 text-sm font-bold text-white">
            GQ
          </div>
          <h1 className="text-2xl font-semibold text-slate-950 mb-2">GreenQuote Pro</h1>
          <h2 className="text-lg font-medium text-slate-700">Welcome Back</h2>
          <p className="text-slate-500 mt-2">Sign in to your account</p>
        </div>

        {error && (
          <Alert
            variant={needsEmailVerification ? undefined : 'destructive'}
            className={needsEmailVerification ? 'mb-6 border-amber-200 bg-amber-50 text-amber-950' : 'mb-6'}
          >
            <AlertDescription>
              <span>{error}</span>
              {needsEmailVerification && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="mt-3 block text-sm font-semibold text-amber-900 underline-offset-4 hover:underline disabled:opacity-60"
                >
                  {resending ? 'Sending verification email...' : 'Resend verification email'}
                </button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {resendMessage && (
          <Alert className="mb-6 border-emerald-200 bg-emerald-50 text-emerald-900">
            <AlertDescription>{resendMessage}</AlertDescription>
          </Alert>
        )}

        {emailConfirmed && !error && (
          <Alert className="mb-6 border-emerald-200 bg-emerald-50 text-emerald-900">
            <AlertDescription>
              Email confirmed. Sign in to finish setting up your GreenQuote account.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              disabled={loading}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              disabled={loading}
              className="mt-1"
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="mr-2 h-4 w-4 rounded border-slate-300 text-emerald-700 focus:ring-emerald-700"
              />
              <span className="text-gray-600">Remember me</span>
            </label>
            <a
              href="#"
              className="font-semibold text-emerald-700 hover:text-emerald-800"
              onClick={(e) => {
                e.preventDefault();
                alert('Password reset functionality coming soon!');
              }}
            >
              Forgot password?
            </a>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Sign up
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500">
            Need help? Contact support@getgreenquote.com
          </p>
        </div>
      </div>
    </div>
  );
}
